import { useMemo, useState } from "react";
import type { EnduserObservation } from "@tellescope/types-models";
import { METRICS, buildSeries, metricValueForObservation, round, sourceEntriesFor, type MetricId } from "./metrics";
import type { useWeightMacroTrackerBlock } from "./useWeightMacroTrackerBlock";
import { mlToOz, type WeightDisplayUnit } from "../../../utils/nutritionUnitConversions";

export interface MetricEntryRow {
  entry: EnduserObservation;
  value: number;
}

type TrackerState = ReturnType<typeof useWeightMacroTrackerBlock>;

export const useMetricDetailView = (state: TrackerState, metricId: MetricId) => {
  const metric = METRICS[metricId];
  const [weightUnit, setWeightUnit] = useState<WeightDisplayUnit>("lb");
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [deletingEntry, setDeletingEntry] = useState<EnduserObservation | undefined>(undefined);

  const series = useMemo(
    () => buildSeries(metric, state.weightEntries, state.foodEntries, state.waterEntries, weightUnit),
    [metric, state.weightEntries, state.foodEntries, state.waterEntries, weightUnit]
  );

  // Mirrors the prototype's effectiveDate derivation exactly: use the raw selection if it still
  // exists in the current (unranged) series, otherwise fall back to the most recent point.
  const effectiveDate = useMemo(() => {
    if (selectedDate && series.some((p) => p.date === selectedDate)) return selectedDate;
    return series.at(-1)?.date ?? null;
  }, [selectedDate, series]);

  const selectedIndex = series.findIndex((p) => p.date === effectiveDate);
  const selectedPoint = series[selectedIndex] ?? null;
  const prevValue = selectedIndex > 0 ? series[selectedIndex - 1]?.value : undefined;
  const isLatest = selectedIndex === series.length - 1;

  const reversedSeries = useMemo(() => [...series].reverse(), [series]);

  // Every individual observation for this metric, across all time — kept separate from `series`
  // (which stays one point per day, for the chart) so edit/delete always act on a real entry
  // instead of an arbitrary stand-in for "the day's total", and so the list isn't limited to
  // whichever single day happens to be selected on the chart.
  const entryRows = useMemo<MetricEntryRow[]>(() => {
    if (metric.kind === "weight") return [];
    return sourceEntriesFor(metric, state.weightEntries, state.foodEntries, state.waterEntries)
      .map((entry) => ({ entry, value: round(metricValueForObservation(metric, entry), metric.decimals) }))
      .sort((a, b) => new Date(b.entry.timestamp).getTime() - new Date(a.entry.timestamp).getTime());
  }, [metric, state.weightEntries, state.foodEntries, state.waterEntries]);

  const targetForMetric: number | undefined = {
    calories: state.targets.dailyCalorieTarget,
    protein: state.targets.dailyProteinTarget,
    carbs: state.targets.dailyCarbTarget,
    fat: state.targets.dailyFatTarget,
    fiber: state.targets.dailyFiberTarget,
    water:
      state.targets.dailyWaterTarget !== undefined
        ? round(mlToOz(state.targets.dailyWaterTarget), METRICS.water.decimals)
        : undefined,
    weight: undefined,
  }[metricId];

  // Weight's series points are already 1:1 with an observation, resolved via the point's own date.
  // Sum-kind metrics act on a specific entryRows item instead — see handleEditEntry/handleDeleteEntryRequest.
  const handleEditWeightPoint = (date: string) => {
    const entryId = series.find((p) => p.date === date)?.entryId;
    if (entryId) state.goToLogEntry(entryId);
  };

  const handleDeleteWeightPointRequest = (date: string) => {
    const entryId = series.find((p) => p.date === date)?.entryId;
    setDeletingEntry(state.weightEntries.find((o) => o.id === entryId));
  };

  const handleEditEntry = (entry: EnduserObservation) => state.goToLogEntry(entry.id);
  const handleDeleteEntryRequest = (entry: EnduserObservation) => setDeletingEntry(entry);

  const handleConfirmDelete = (id: string) => state.deleteEntry(id);

  return {
    metric,
    weightUnit,
    setWeightUnit,
    series,
    reversedSeries,
    entryRows,
    effectiveDate,
    setSelectedDate,
    selectedPoint,
    prevValue,
    isLatest,
    target: targetForMetric,
    deletingEntry,
    setDeletingEntry,
    handleEditWeightPoint,
    handleDeleteWeightPointRequest,
    handleEditEntry,
    handleDeleteEntryRequest,
    handleConfirmDelete,
  };
};
